// fluid-properties.js - Core thermodynamic calculation module

/**
 * Constants and reference data for natural gas components
 */
export const COMPONENTS = {
  METHANE: {
    name: "Methane",
    formula: "CH4",
    MW: 16.04,  // g/mol
    Tc: 190.56, // K
    Pc: 45.99,  // bar
    omega: 0.011, // acentric factor
    Tb: 111.66, // K (boiling point)
    Vc: 0.099,  // m³/kmol (critical volume)
    Zc: 0.286,  // critical compressibility factor
    cp: [19.25, 0.05213, 1.197e-5, -1.132e-8] // heat capacity coefficients
  },
  ETHANE: {
    name: "Ethane",
    formula: "C2H6",
    MW: 30.07,
    Tc: 305.32,
    Pc: 48.72,
    omega: 0.099,
    Tb: 184.55,
    Vc: 0.148,
    Zc: 0.285,
    cp: [5.409, 0.17811, -6.938e-5, 8.713e-9]
  },
  PROPANE: {
    name: "Propane",
    formula: "C3H8",
    MW: 44.10,
    Tc: 369.83,
    Pc: 42.48,
    omega: 0.152,
    Tb: 231.05,
    Vc: 0.203,
    Zc: 0.281,
    cp: [-4.224, 0.30679, -1.586e-4, 3.215e-8]
  },
  BUTANE: {
    name: "n-Butane",
    formula: "C4H10",
    MW: 58.12,
    Tc: 425.12,
    Pc: 37.96,
    omega: 0.199,
    Tb: 272.65,
    Vc: 0.255,
    Zc: 0.274,
    cp: [-0.956, 0.39122, -2.211e-4, 4.989e-8]
  },
  PENTANE: {
    name: "n-Pentane",
    formula: "C5H12",
    MW: 72.15,
    Tc: 469.7,
    Pc: 33.7,
    omega: 0.251,
    Tb: 309.22,
    Vc: 0.313,
    Zc: 0.270,
    cp: [-3.626, 0.48736, -2.599e-4, 5.305e-8]
  },
  HEXANE: {
    name: "n-Hexane",
    formula: "C6H14",
    MW: 86.18,
    Tc: 507.6,
    Pc: 30.25,
    omega: 0.301,
    Tb: 341.88,
    Vc: 0.371,
    Zc: 0.266,
    cp: [-5.151, 0.59775, -3.282e-4, 7.243e-8]
  },
  HEPTANE: {
    name: "n-Heptane",
    formula: "C7H16",
    MW: 100.20,
    Tc: 540.2,
    Pc: 27.40,
    omega: 0.349,
    Tb: 371.58,
    Vc: 0.428,
    Zc: 0.261,
    cp: [-5.146, 0.67786, -3.748e-4, 8.312e-8]
  },
  OCTANE: {
    name: "n-Octane",
    formula: "C8H18",
    MW: 114.23,
    Tc: 568.7,
    Pc: 24.90,
    omega: 0.400,
    Tb: 398.82,
    Vc: 0.486,
    Zc: 0.259,
    cp: [-6.096, 0.76416, -4.290e-4, 9.500e-8]
  },
  NONANE: {
    name: "n-Nonane",
    formula: "C9H20",
    MW: 128.26,
    Tc: 594.6,
    Pc: 22.90,
    omega: 0.444,
    Tb: 423.97,
    Vc: 0.544,
    Zc: 0.255,
    cp: [-6.932, 0.85200, -4.826e-4, 1.073e-7]
  },
  NITROGEN: {
    name: "Nitrogen",
    formula: "N2",
    MW: 28.01,
    Tc: 126.2,
    Pc: 33.98,
    omega: 0.040,
    Tb: 77.35,
    Vc: 0.090,
    Zc: 0.291,
    cp: [28.98, -0.00157, 8.081e-6, -2.873e-9]
  },
  CO2: {
    name: "Carbon Dioxide",
    formula: "CO2",
    MW: 44.01,
    Tc: 304.13,
    Pc: 73.77,
    omega: 0.228,
    Tb: 194.69,
    Vc: 0.094,
    Zc: 0.274,
    cp: [19.8, 0.07344, -5.602e-5, 1.715e-8]
  },
  H2S: {
    name: "Hydrogen Sulfide",
    formula: "H2S",
    MW: 34.08,
    Tc: 373.2,
    Pc: 89.63,
    omega: 0.100,
    Tb: 212.85,
    Vc: 0.098,
    Zc: 0.284,
    cp: [31.94, 0.00144, 2.432e-5, -1.176e-8]
  },
  WATER: {
    name: "Water",
    formula: "H2O",
    MW: 18.02,
    Tc: 647.14,
    Pc: 220.64,
    omega: 0.344,
    Tb: 373.15,
    Vc: 0.056,
    Zc: 0.229,
    cp: [32.24, 0.00192, 1.056e-5, -3.596e-9]
  }
};

// Universal gas constant (various units)
export const R = {
  BAR_L_MOL_K: 0.08314, // bar·L/(mol·K)
  KPA_M3_KMOL_K: 8.314,  // kPa·m³/(kmol·K)
  J_MOL_K: 8.314,       // J/(mol·K)
  BTU_LBMOL_R: 1.987    // BTU/(lbmol·°R)
};

/**
 * Calculate properties of a gas mixture using Kay's mixing rules
 * @param {Array} components - Array of component objects
 * @param {Array} compositions - Array of mole fractions (should sum to 1)
 * @returns {Object} Mixed properties
 */
export function calculateMixtureProperties(components, compositions) {
  if (components.length !== compositions.length) {
    throw new Error("Components and compositions arrays must have the same length");
  }
  
  // Normalize compositions if they don't sum to 1
  const sum = compositions.reduce((acc, val) => acc + val, 0);
  const normalizedCompositions = compositions.map(comp => comp / sum);
  
  // Calculate pseudo-critical properties using Kay's mixing rules
  let Tc_mix = 0;
  let Pc_mix = 0;
  let MW_mix = 0;
  let omega_mix = 0;
  
  for (let i = 0; i < components.length; i++) {
    Tc_mix += normalizedCompositions[i] * components[i].Tc;
    Pc_mix += normalizedCompositions[i] * components[i].Pc;
    MW_mix += normalizedCompositions[i] * components[i].MW;
    omega_mix += normalizedCompositions[i] * components[i].omega;
  }
  
  return {
    Tc: Tc_mix,
    Pc: Pc_mix,
    MW: MW_mix,
    omega: omega_mix,
    compositions: normalizedCompositions
  };
}

/**
 * Calculate the compressibility factor (Z) using Peng-Robinson EOS
 * @param {number} T - Temperature in K
 * @param {number} P - Pressure in bar
 * @param {number} Tc - Critical temperature in K
 * @param {number} Pc - Critical pressure in bar
 * @param {number} omega - Acentric factor
 * @returns {Object} Compressibility factor and related parameters
 */
export function pengRobinsonZ(T, P, Tc, Pc, omega) {
  // Calculate reduced temperature and pressure
  const Tr = T / Tc;
  // eslint-disable-next-line no-unused-vars
  const Pr = P / Pc;
  
  // Calculate a and b parameters
  const kappa = 0.37464 + 1.54226 * omega - 0.26992 * omega * omega;
  const alpha = Math.pow(1 + kappa * (1 - Math.sqrt(Tr)), 2);
  
  const a = 0.45724 * (R.BAR_L_MOL_K * R.BAR_L_MOL_K) * (Tc * Tc) / Pc * alpha;
  const b = 0.07780 * R.BAR_L_MOL_K * Tc / Pc;
  
  // Solve the cubic equation for Z
  const A = a * P / (R.BAR_L_MOL_K * T) ** 2;
  const B = b * P / (R.BAR_L_MOL_K * T);
  
  // Cubic equation coefficients: Z³ - (1-B)Z² + (A-3B²-2B)Z - (AB-B²-B³) = 0
  const coef1 = 1;
  const coef2 = -(1 - B);
  const coef3 = (A - 3 * B * B - 2 * B);
  const coef4 = -(A * B - B * B - B * B * B);
  
  // Solve cubic equation for Z
  const roots = solveCubic(coef1, coef2, coef3, coef4);
  
  // For gases, we typically want the largest root (vapor phase)
  const Z = Math.max(...roots);
  
  return { 
    Z,
    a,
    b,
    alpha,
    kappa,
    A,
    B,
    Tc,   // needed by calculateEnthalpyDeparture for dAlpha/dT
    allRoots: roots
  };
}

/**
 * Calculate entropy departure for real gas using Peng-Robinson EOS
 * @param {number} T - Temperature in K
 * @param {number} P - Pressure in bar
 * @param {number} Z - Compressibility factor
 * @param {Object} prParams - Peng-Robinson parameters
 * @returns {number} Entropy departure in J/(mol·K)
 */
export function calculateEntropyDeparture(T, Z, prParams) {
  const { a, b, alpha, kappa, B } = prParams;
  const sqrt2 = Math.sqrt(2);
  
  // dAlpha/dT
  const dAlphaDT = -kappa * (1 + kappa * (1 - Math.sqrt(T / prParams.Tc))) / 
                   Math.sqrt(T * prParams.Tc);
  
  // S_res = R*ln(Z-B) + (a / (2*sqrt(2)*b)) * (1/alpha * dAlphaDT) * ln((Z+(1+sqrt(2))B)/(Z+(1-sqrt(2))B))
  // (Standard PR entropy departure formula)
  const term1 = R.J_MOL_K * Math.log(Math.max(1e-10, Z - B));
  const term2 = (a / (2 * sqrt2 * b)) * (dAlphaDT / alpha) * 
                Math.log((Z + (1 + sqrt2) * B) / Math.max(1e-10, (Z + (1 - sqrt2) * B)));
  
  return term1 + term2;
}

/**
 * Calculate ideal gas entropy change relative to T0, P0
 */
export function calculateIdealGasEntropyChange(cpCoeffs, T1, T2, P1, P2) {
  const [A, B, C, D] = cpCoeffs;
  
  // ΔS_ig = ∫(Cp/T) dT - R*ln(P2/P1)
  // ∫(A/T + B + C*T + D*T²) dT = A*ln(T2/T1) + B*(T2-T1) + C/2*(T2²-T1²) + D/3*(T2³-T1³)
  const termT = A * Math.log(T2 / T1) + 
                B * (T2 - T1) + 
                (C / 2) * (T2 * T2 - T1 * T1) + 
                (D / 3) * (T2 * T2 * T2 - T1 * T1 * T1);
  
  const termP = R.J_MOL_K * Math.log(P2 / P1);
  
  return termT - termP;
}

/**
 * Calculate absolute entropy (relative to ref T0=273.15, P0=1.01325)
 */
export function calculateEntropy(T, P, components, compositions) {
  const T0 = 273.15;
  const P0 = 1.01325;
  
  let S_ig_total = 0;
  let Tc_mix = 0, Pc_mix = 0, omega_mix = 0;

  for (let i = 0; i < components.length; i++) {
    const zi = compositions[i];
    S_ig_total += zi * calculateIdealGasEntropyChange(components[i].cp, T0, T, P0, P);
    // Entropy of mixing: -R * Σ zi * ln(zi)
    if (zi > 1e-10) S_ig_total -= zi * R.J_MOL_K * Math.log(zi);
    
    Tc_mix += zi * components[i].Tc;
    Pc_mix += zi * components[i].Pc;
    omega_mix += zi * components[i].omega;
  }
  
  const pr = pengRobinsonZ(T, P, Tc_mix, Pc_mix, omega_mix);
  const S_res = calculateEntropyDeparture(T, pr.Z, pr);
  
  return S_ig_total + S_res;
}

/**
 * Calculate density based on compressibility factor
 * @param {number} MW - Molecular weight in g/mol
 * @param {number} P - Pressure in bar
 * @param {number} Z - Compressibility factor
 * @param {number} T - Temperature in K
 * @returns {number} Density in kg/m³
 */
export function calculateDensity(MW, P, Z, T) {
  // P = Z·ρ·R·T/MW → ρ = P·MW/(Z·R·T)
  // Convert L to m³ and mol to kmol
  return P * MW / (Z * R.BAR_L_MOL_K * T) * 1000 / 1000;
}

/**
 * Calculate enthalpy departure for real gas using Peng-Robinson EOS
 * @param {number} T - Temperature in K
 * @param {number} Z - Compressibility factor
 * @param {Object} prParams - Peng-Robinson parameters (from pengRobinsonZ function)
 * @returns {number} Enthalpy departure in J/mol
 */
export function calculateEnthalpyDeparture(T, Z, prParams) {
  const { a, b, alpha, kappa, B } = prParams;
  
  // Calculate derivative of alpha with respect to temperature
  const dAlphaDT = -kappa * (1 + kappa * (1 - Math.sqrt(T / prParams.Tc))) / 
                   Math.sqrt(T * prParams.Tc);
  
  // Calculate enthalpy departure
  const term1 = R.J_MOL_K * T * (Z - 1);
  const term2 = (a / (2 * Math.sqrt(2) * b)) * 
                (1 + (T / alpha) * dAlphaDT) * 
                Math.log((Z + (1 + Math.sqrt(2)) * B) / (Z + (1 - Math.sqrt(2)) * B));
  
  return term1 - term2;
}

/**
 * Calculate ideal gas enthalpy change
 * @param {Array} cpCoeffs - Heat capacity coefficients [A, B, C, D] for Cp = A + B*T + C*T² + D*T³
 * @param {number} T1 - Initial temperature in K
 * @param {number} T2 - Final temperature in K
 * @returns {number} Enthalpy change in J/mol
 */
export function calculateIdealGasEnthalpyChange(cpCoeffs, T1, T2) {
  const [A, B, C, D] = cpCoeffs;
  
  // ∫Cp dT from T1 to T2
  const term1 = A * (T2 - T1);
  const term2 = (B / 2) * (T2 * T2 - T1 * T1);
  const term3 = (C / 3) * (T2 * T2 * T2 - T1 * T1 * T1);
  const term4 = (D / 4) * (T2 * T2 * T2 * T2 - T1 * T1 * T1 * T1);
  
  return term1 + term2 + term3 + term4;
}

/**
 * Calculate isentropic enthalpy change for compression/expansion
 * @param {number} T1 - Initial temperature in K
 * @param {number} P1 - Initial pressure in bar
 * @param {number} P2 - Final pressure in bar
 * @param {number} Z1 - Initial compressibility factor
 * @param {number} gamma - Specific heat ratio Cp/Cv
 * @returns {number} Temperature after isentropic compression/expansion
 */
export function calculateIsentropicTemperature(T1, P1, P2, Z1, gamma) {
  // For real gas, this is an approximation
  return T1 * Math.pow(P2 / P1, (gamma - 1) / gamma);
}

/**
 * Calculate compression power requirement
 * @param {number} massFlow - Mass flow rate in kg/s
 * @param {number} h1 - Initial enthalpy in J/kg
 * @param {number} h2s - Isentropic final enthalpy in J/kg
 * @param {number} efficiency - Compressor efficiency (fraction)
 * @returns {number} Power requirement in kW
 */
export function calculateCompressionPower(massFlow, h1, h2s, efficiency) {
  // Power = mass flow * (h2 - h1) / efficiency
  // where h2 is the actual enthalpy after compression
  const h2 = h1 + (h2s - h1) / efficiency;
  return massFlow * (h2 - h1) / 1000; // Convert W to kW
}

/**
 * Calculate vapor-liquid equilibrium using Peng-Robinson EOS
 * @param {Object} mixture - Mixture properties
 * @param {number} T - Temperature in K
 * @param {number} P - Pressure in bar
 * @returns {Object} VLE results with liquid and vapor compositions
 */
export function calculateVLE(mixture, T, P) {
  // This is a simplified implementation
  // A complete implementation would use flash calculations
  
  // For now, return a placeholder result
  return {
    vaporFraction: 0.8,
    liquidComposition: mixture.compositions.map(x => x * 0.5),
    vaporComposition: mixture.compositions.map(x => x * 1.2)
  };
}

/**
 * Solve a cubic equation of the form ax³ + bx² + cx + d = 0
 * @param {number} a - Coefficient of x³
 * @param {number} b - Coefficient of x²
 * @param {number} c - Coefficient of x
 * @param {number} d - Constant term
 * @returns {Array} Array of real roots
 */
function solveCubic(a, b, c, d) {
  // Convert to depressed cubic t³ + pt + q = 0 via substitution x = t - b/(3a)
  const p = (3 * a * c - b * b) / (3 * a * a);
  const q = (2 * b * b * b - 9 * a * b * c + 27 * a * a * d) / (27 * a * a * a);
  
  // Discriminant
  const discriminant = (q * q / 4) + (p * p * p / 27);
  
  let roots = [];
  
  if (discriminant > 0) {
    // One real root, two complex roots
    const u = Math.cbrt(-q / 2 + Math.sqrt(discriminant));
    const v = Math.cbrt(-q / 2 - Math.sqrt(discriminant));
    
    const root = u + v - b / (3 * a);
    roots = [root, root, root]; // Return the real root three times for simplicity
  } 
  else {
    // Three real roots
    const angle = Math.acos(-q / 2 / Math.sqrt(-p * p * p / 27));
    const r = 2 * Math.sqrt(-p / 3);
    
    roots = [
      r * Math.cos(angle / 3) - b / (3 * a),
      r * Math.cos((angle + 2 * Math.PI) / 3) - b / (3 * a),
      r * Math.cos((angle + 4 * Math.PI) / 3) - b / (3 * a)
    ];
  }
  
  return roots;
}

/**
 * Calculate specific heat ratio (Cp/Cv) for a gas
 * @param {Array} cpCoeffs - Heat capacity coefficients
 * @param {number} T - Temperature in K
 * @param {number} Z - Compressibility factor
 * @returns {number} Specific heat ratio (gamma)
 */
export function calculateGamma(cpCoeffs, T, Z) {
  // Calculate ideal-gas Cp at temperature T  [J/(mol·K)]
  const [A, B, C, D] = cpCoeffs;
  const Cp_ig = A + B * T + C * T * T + D * T * T * T;

  // Real-gas correction: Cv_real = Cp_ig - R*Z  (first-order departure)
  // For an ideal gas Z=1 this reduces to the classical Cp - R.
  const Cv_real = Cp_ig - R.J_MOL_K * Z;

  // Guard against non-physical Cv (can happen at very high P/low T)
  const Cv_safe = Math.max(Cv_real, R.J_MOL_K * 0.5);

  return Cp_ig / Cv_safe;
}

/**
 * Calculate heat exchanger duty
 * @param {number} massFlow - Mass flow rate in kg/s
 * @param {number} cp - Specific heat capacity in J/(kg·K)
 * @param {number} T1 - Inlet temperature in K
 * @param {number} T2 - Outlet temperature in K
 * @returns {number} Heat duty in kW
 */
export function calculateHeatExchangerDuty(massFlow, cp, T1, T2) {
  return massFlow * cp * (T2 - T1) / 1000; // Convert W to kW
}